import moment from "moment";
import { useEffect, useState } from "react";

import americanExpressIcon from "../../../assets/img/common/american-express.svg";
import applePayIcon from "../../../assets/img/common/apple-pay.svg";
import masterCardIcon from "../../../assets/img/common/master-card.svg";
import paypalIcon from "../../../assets/img/common/paypal.svg";
import visaIcon from "../../../assets/img/common/visa.svg";
import minus from "../../../assets/img/icons/minus.svg";
import plus from "../../../assets/img/icons/plus_black_2.svg";
import secureIcon from "../../../assets/img/icons/secure-payment.svg";
import BackButtonComponent from "../../../components/BackButtonComponent/BackButtonComponent";
import ButtonLoader from "../../../components/DropdownLoader/DropdownLoader";
import {
  AdditionalUserInput,
  AdditionalUserInputSection,
  BillingContainer,
  ColoredDot,
  InputButton,
  LinkSpan,
  OrderSummarySection,
  PaymentCard,
  SaveTag,
  ToggleSection,
  ToggleText,
  UpgradeLowerSection,
  UpgradeUpperSection,
  UserNumberBox,
} from "../../../styledComponents/billing";
import { PrimaryButton } from "../../../styledComponents/buttons";
import {
  CommonGrid,
  CommonText,
  FirstWordRound,
  FormAlert,
  InputField,
  InputLabel,
  PageTitle,
} from "../../../styledComponents/common";
import {
  ToggleButton,
  ToggleButtonRound,
} from "../../../styledComponents/invoice";
import {
  CouponAlert,
  SecurePaymentContainer,
  SecurePaymentIcon,
  SecurePaymentText,
  YearlySaveBadge,
} from "../subscriptionStyles";
import PaymentModal from "./PaymentModal";

const RoundLetter = ({ text, color, isActive }) => {
  return (
    <FirstWordRound
      backColor={isActive ? color : "#84919e"}
      size="50px"
      fontSize="26px"
    >
      {" "}
      {text.slice(0, 1).toUpperCase()}{" "}
    </FirstWordRound>
  );
};

const Checkout = props => {
  const [planInterval, setPlanInterval] = useState("yearly");
  const [planData, setPlanData] = useState(null);
  const [usersNumber, setUsersNumber] = useState(0);
  const [prepurchasedCount, setPrepurchasedCount] = useState(0);
  const [totalSeats, setTotalSeats] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [discountPlan, setDiscountPlan] = useState(null);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountedTotal, setDiscountedTotal] = useState(0);
  const [coupon, setCoupon] = useState("");
  const [couponError, setCouponError] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);

  const {
    subscriptionUpdated,
    selectedOrganization,
    clearCurrentSubscription,
    clearSubscriptionUpdated,
    discountInfo,
    clearDiscountInfo,
    clearDiscountErrorMessage,
    discountErrorMessage,
    appOnboardingState,
  } = props;

  const onboardingDiscountCode = appOnboardingState?.discountCode || null;
  const onboardingDiscountExpiresAt =
    appOnboardingState?.discountExpiresAt || null;
  const onboardingDiscountValid = Boolean(
    appOnboardingState?.isCompleted &&
      appOnboardingState?.discountEarned &&
      !appOnboardingState?.discountUsed &&
      onboardingDiscountCode &&
      onboardingDiscountExpiresAt &&
      moment()
        .tz(selectedOrganization?.timezone || "UTC")
        .isBefore(
          moment.tz(
            onboardingDiscountExpiresAt,
            selectedOrganization?.timezone || "UTC",
          ),
        ),
  );

  useEffect(() => {
    if (
      props.location?.state?.interval &&
      props.location?.state?.[props.location?.state?.interval]
    ) {
      const planInfo = props.location.state[props.location.state.interval];
      setPlanInterval(props.location.state.interval);
      setPlanData(planInfo);
      setUsersNumber(planInfo.currentUsers);
    } else {
      props.history.goBack();
    }

    return () => {
      clearCurrentSubscription();
      clearSubscriptionUpdated();
      // clearDiscountInfo();
      clearDiscountErrorMessage();
    };
  }, []);

  useEffect(() => {
    setCoupon("");
    setCouponError("");
  }, [planInterval]);

  useEffect(() => {
    if (planData && planData.amount) {
      let seats = usersNumber;
      let total = 0.0;
      if (seats > 1) {
        seats = seats - 1;
      }
      total = (seats * planData.amount).toFixed(2);
      setTotalAmount(total);
      let discount = 0;
      if (
        discountPercentage &&
        discountPercentage > 0 &&
        discountPlan &&
        discountPlan.interval === planInterval
      ) {
        discount += parseFloat(
          (total * (discountPercentage / 100)).toFixed(2),
        );
      }
      if (onboardingDiscountValid) {
        discount += 10;
      }
      if (discount > 0) {
        setDiscountAmount(discount.toFixed(2));
        total = (total - discount).toFixed(2);
      } else {
        setDiscountAmount(0);
      }

      setTotalSeats(seats);
      setDiscountedTotal(total);
    }
  }, [planInterval, planData, usersNumber, discountPercentage, onboardingDiscountValid]);

  useEffect(() => {
    if (
      subscriptionUpdated &&
      selectedOrganization &&
      selectedOrganization.id &&
      planData &&
      planData.paddleId
    ) {
      const plan = {
        organizationName: selectedOrganization.name,
        organizationId: selectedOrganization.id,
        organizationColor: selectedOrganization.color,
        isOrgActive: selectedOrganization.is_active,
        currentUsers: usersNumber,
        paddleId: planData.paddleId,
      };
      props.history.push({
        pathname: "/user/paddle-payment-checkout",
        state: { plan },
      });
      setTimeout(() => {
        props.clearSubscriptionUpdated();
      }, 200);
    }
  }, [subscriptionUpdated]);

  useEffect(() => {
    if (
      discountInfo &&
      discountInfo.id &&
      discountInfo.organization_id &&
      discountInfo.plan &&
      planData &&
      planData.id &&
      selectedOrganization &&
      selectedOrganization.id &&
      selectedOrganization.id === discountInfo.organization_id
    ) {
      if (discountInfo.plan.id === planData.id) {
        if (discountInfo.type === "percentage" && discountInfo.percentage_off) {
          setDiscountPercentage(discountInfo.percentage_off);
          setDiscountPlan(discountInfo.plan);
          setCoupon("");
        }
      }
    } else if (
      discountInfo &&
      discountInfo.organization_id &&
      selectedOrganization &&
      selectedOrganization.id &&
      selectedOrganization.id !== discountInfo.organization_id
    ) {
      clearDiscountInfo();
    }
  }, [discountInfo, planData, planInterval]);

  useEffect(() => {
    if (discountErrorMessage) {
      setCouponError(discountErrorMessage);
    }
  }, [discountErrorMessage]);

  const onToggleInterval = interval => {
    setPlanInterval(interval);
    setPlanData(props.location.state[interval]);
  };

  const onUsersNumberChange = e => {
    setPrepurchasedCount(e.target.value);
  };

  const onCouponChange = e => {
    setCouponError("");
    setCoupon(e.target.value);
  };

  const popupToggle = () => {
    setPopupOpen(!popupOpen);
  };

  const handleButtonDisabled = () => {
    if (
      props.location?.state?.currentPlan?.id === planData?.id &&
      props.location?.state?.currentPlan?.status !== "trialing"
    ) {
      return true;
    }
    return false;
  };

  const handleApplyCoupon = () => {
    if (!coupon) {
      setCouponError("Coupon can not be empty");
    } else if (
      coupon &&
      props.selectedOrganization &&
      props.selectedOrganization.id
    ) {
      const payload = {
        email: localStorage.getItem("email"),
        codes: [coupon],
        organization_id: props.selectedOrganization.id,
        selected_plan_id: planData && planData.id ? planData.id : null,
        selected_plan_interval: planData?.interval,
      };

      props.applyCoupon(payload);
    }
  };

  const confirmPayment = () => {
    if (
      selectedOrganization &&
      selectedOrganization.id &&
      planData &&
      planData.paddleId &&
      (props.location?.state?.currentPlan?.id !== planData.id ||
        props.location?.state?.currentPlan?.status === "trialing")
    ) {
      const plan = {
        organizationName: selectedOrganization.name,
        organizationId: selectedOrganization.id,
        organizationColor: selectedOrganization.color,
        isOrgActive: selectedOrganization.is_active,
        currentUsers: usersNumber,
        paddleId: planData.paddleId,
        id: planData.id,
      };
      props.history.push({
        pathname: "/user/paddle-payment-checkout",
        state: { plan },
      });
    }
  };

  return (
    <div className="content">
      <PageTitle>Billing - Checkout</PageTitle>
      {/* <PaymentModal
        isOpen={popupOpen}
        toggle={popupToggle}
        toggleInterval={onToggleInterval}
        planInterval={planInterval}
        planData={planData}
        totalSeats={totalSeats}
        discountPercentage={discountPercentage}
        discountPlan={discountPlan}
        discountedTotal={discountedTotal}
        confirmPayment={confirmPayment}
        isLoading={props.updateSubscriptionIsLoading}
      /> */}
      <BillingContainer>
        <div>
          <UpgradeUpperSection>
            <BackButtonComponent
              onClick={() => props.history.goBack()}
              subTitle="Go back to plans"
            />
            <CommonGrid
              columns="auto"
              gap="15px"
              justifyContent="center"
              style={{ justifyItems: `center` }}
            >
              {/* {selectedOrganization && selectedOrganization.name && (
                <RoundLetter
                  text={selectedOrganization.name}
                  color={
                    selectedOrganization.color
                      ? selectedOrganization.color
                      : "#9A96F9"
                  }
                  isActive={selectedOrganization.is_active}
                />
              )} */}
              <CommonText name title fontSize="20px">
                {planData ? `Upgrade to ${planData.name}` : `Change Plan`}
              </CommonText>
              <ToggleSection isCheckout>
                <ToggleText
                  active={planInterval === "yearly"}
                  onClick={() => onToggleInterval("yearly")}
                >
                  <YearlySaveBadge isActive={planInterval === "yearly"}>
                    Save 40%
                  </YearlySaveBadge>
                  Yearly
                </ToggleText>

                <ToggleText
                  active={planInterval === "monthly"}
                  onClick={() => onToggleInterval("monthly")}
                >
                  Monthly
                </ToggleText>
              </ToggleSection>
            </CommonGrid>
            <div />
          </UpgradeUpperSection>
          <UpgradeLowerSection>
            <div>
              <InputLabel fontSize="14px">Current Users</InputLabel>
              <CommonText fontSize="13px" style={{ marginTop: `10px` }}>
                Current number of active members in your organization
              </CommonText>
              <UserNumberBox>
                {usersNumber > 1 ? usersNumber - 1 : usersNumber}
              </UserNumberBox>

              {couponError && <CouponAlert>{couponError}</CouponAlert>}
              <InputLabel fontSize="14px" margin="25px 0 0 0">
                Order Summary
              </InputLabel>
              <OrderSummarySection>
                <ColoredDot color="#9A96F9" />
                <CommonText fontSize="13px">
                  Your total amount due may change based on the VAT/Sales Tax
                </CommonText>
                <ColoredDot />
                <CommonText fontSize="13px">
                  Your payment method will be charged{" "}
                  <span style={{ fontWeight: `600` }}>{planInterval}</span>{" "}
                  starting{" "}
                  <span style={{ fontWeight: `600` }}>
                    {moment(new Date()).format("MMMM D, YYYY")}
                  </span>
                </CommonText>
              </OrderSummarySection>
            </div>
            <PaymentCard>
              <CommonGrid
                padding={
                  discountAmount &&
                  discountAmount > 0 &&
                  discountPlan &&
                  discountPlan.interval === planInterval
                    ? "30px 30px 20px 30px"
                    : "30px 30px 40px 30px"
                }
                style={{
                  borderBottom:
                    discountAmount &&
                    discountAmount > 0 &&
                    discountPlan &&
                    discountPlan.interval === planInterval
                      ? `none`
                      : `1px solid #EAEEF7`,
                }}
              >
                <CommonText name>
                  {totalSeats} Users × $
                  {planData && planData.amount && planInterval === "yearly"
                    ? (planData.amount / 12).toFixed(1)
                    : planData?.amount}
                  {planInterval === "yearly" && ` X 12`}
                </CommonText>
                <CommonText name title>
                  ${totalAmount}
                </CommonText>
              </CommonGrid>
              {discountAmount &&
              discountAmount > 0 &&
              (onboardingDiscountValid ||
                (discountPlan && discountPlan.interval === planInterval)) ? (
                <CommonGrid
                  padding="20px 30px 20px 30px"
                  style={{ borderBottom: `1px solid #EAEEF7` }}
                >
                  <CommonText color='#be0215' name>
                    {discountPercentage > 0 &&
                    discountPlan &&
                    discountPlan.interval === planInterval ? (
                      onboardingDiscountValid ? (
                        `Discount (${discountPercentage}% off + $10 onboarding)`
                      ) : (
                        `Discount (${discountPercentage}% off)`
                      )
                    ) : onboardingDiscountValid ? (
                      `Onboarding Discount`
                    ) : (
                      `Discount`
                    )}
                  </CommonText>
                  <CommonText color='#be0215'  name title>- ${discountAmount}</CommonText>
                </CommonGrid>
              ) : null}
              <CommonGrid columns="1fr" padding="30px" gap="30px">
                <CommonGrid>
                  <CommonText name title>
                    Total{" "}
                    <span style={{ color: `#fe5969` }}>(Excluding VAT)</span>
                  </CommonText>
                  <CommonText name title>
                    ${discountedTotal}/{" "}
                    {planInterval === "yearly" ? "Year" : "Month"}
                  </CommonText>
                </CommonGrid>
                <CommonText name>
                  * VAT/ Sales taxes will be added in checkout based on
                  location.
                </CommonText>
                <CommonGrid columns="1fr" gap="10px">
                  <PrimaryButton
                    fontSize="16px"
                    height="46px"
                    disabled={handleButtonDisabled()}
                    onClick={() => confirmPayment()}
                  >
                    Continue Payment
                  </PrimaryButton>
                  {handleButtonDisabled() && (
                    <FormAlert>You are already on this plan.</FormAlert>
                  )}
                </CommonGrid>
                <SecurePaymentContainer>
                  <SecurePaymentIcon src={secureIcon} alt="" />
                  <CommonText margin="0 0 0 8px">Secure Payments</CommonText>
                </SecurePaymentContainer>
                <CommonText $label style={{ marginTop: `0` }}>
                  We accept following cards
                </CommonText>
                <CommonGrid
                  columns="repeat(5, auto)"
                  justifyContent="start"
                  style={{ marginBottom: `15px` }}
                >
                  <img src={visaIcon} alt="visa" />
                  <img src={masterCardIcon} alt="Mastercard" />
                  <img src={paypalIcon} alt="paypal" />
                  <img src={applePayIcon} alt="apple pay" />
                  <img src={americanExpressIcon} alt="american express" />
                </CommonGrid>
              </CommonGrid>
            </PaymentCard>
          </UpgradeLowerSection>
        </div>
      </BillingContainer>
    </div>
  );
};

export default Checkout;
